import styled, { css } from 'styled-components';
import { ButtonContainer as ChoiceGrid } from 'src/apps/LearningSession/Common/learnableCards/TestLearnableCard/Response/MCResponse/styles';
import {
  Button as ChoiceButton,
  ButtonChildren as ChoiceText,
  PositionLabel,
} from 'src/apps/LearningSession/Common/learnableCards/TestLearnableCard/Response/MCResponse/TestButton/styles';
import { Eyebrow, overlayTextShadow } from '../sharedStyles';

export { HeaderFlower } from '../sharedStyles';

export const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.sz.s2};
  `,
);

export const Heading = styled.h2<{ $showBigger?: boolean }>(
  ({ theme, $showBigger }) => css`
    ${$showBigger ? theme.typography.heading.desktop.m : theme.typography.heading.desktop.s};
    margin: 0;
    color: ${theme.co.neutral10};
    overflow-wrap: anywhere;
  `,
);

export const PromptImage = styled.img(
  ({ theme }) => css`
    max-width: ${theme.sz.xs};
    max-height: ${theme.sz.s48};
    border-radius: ${theme.bo.medium};
    object-fit: contain;
  `,
);

export const Attributes = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.sz.s2};
  `,
);

export const Response = styled.div<{
  $isMultipleChoice: boolean;
  $showBiggerChoices: boolean;
  $overlay: boolean;
}>(
  ({ theme, $isMultipleChoice, $showBiggerChoices, $overlay }) => css`
    width: 100%;
    max-width: ${$overlay ? 'none' : theme.sz.ms};

    ${
      $isMultipleChoice &&
      css`
        container-type: inline-size;

        ${ChoiceGrid} {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        ${ChoiceButton} {
          padding-inline: ${theme.sz.s3};
          font-size: ${$showBiggerChoices ? theme.DEPRECATED_ty.larger : theme.DEPRECATED_ty.large};
          line-height: 1.25;
          hyphens: manual;
          overflow-wrap: break-word;
          text-wrap: balance;
        }

        ${PositionLabel} {
          left: ${theme.sz.s3};
        }

        @media (min-width: ${theme.sz.md}) {
          ${ChoiceText} {
            margin-inline: ${theme.sz.s8};
          }
        }

        /* Narrow columns (e.g. tablets) drop the keyboard number badges so the text gets the room */
        @container (max-width: 31rem) {
          ${ChoiceButton} {
            font-size: ${
              $showBiggerChoices ? theme.DEPRECATED_ty.large : theme.DEPRECATED_ty.mediumLarge
            };
          }

          ${PositionLabel} {
            display: none;
          }

          ${ChoiceText} {
            margin-inline: 0;
          }
        }
      `
    }

    ${
      $isMultipleChoice &&
      $overlay &&
      css`
        ${ChoiceGrid} {
          gap: ${theme.sz.s2};
        }

        ${ChoiceButton} {
          min-height: ${theme.sz.s12};
          padding: ${theme.sz.s2} ${theme.sz.s3};
          border-radius: ${theme.bo.mediumSmall};
          box-shadow: none;
          font-size: ${$showBiggerChoices ? theme.DEPRECATED_ty.large : theme.DEPRECATED_ty.medium};

          &:not(:disabled) {
            border-color: transparent;
          }

          &:hover,
          &:active {
            box-shadow: none;
            transform: none;
          }
        }
      `
    }
  `,
);

export const PostAnswerInfo = styled.p<{ $visible: boolean }>(
  ({ theme, $visible }) => css`
    ${theme.typography.paragraph.base};
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.sz.s2};
    margin: 0;
    color: ${theme.co.neutral10};
    visibility: ${$visible ? 'visible' : 'hidden'};
  `,
);

export const PostAnswerLabel = styled.span(
  ({ theme }) => css`
    color: ${theme.co.neutral40};
  `,
);

export const NextAction = styled.div<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    align-items: center;
    justify-content: ${$overlay ? 'center' : 'flex-start'};
    /* Match I don’t know / Check answer / Continue so swapping them doesn’t shift layout */
    min-height: ${theme.sz.s11};

    /* Button wraps in a container; skip the overlay swipe-hint <button> */
    & > div button {
      min-width: ${theme.sz.s40};
    }
  `,
);

export { Eyebrow };

export const Content = styled.div<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    flex-direction: column;
    gap: ${$overlay ? theme.sz.s4 : theme.sz.s6};
    width: 100%;

    ${
      $overlay &&
      css`
        ${Eyebrow}, ${Heading}, ${PostAnswerInfo}, ${PostAnswerLabel} {
          color: ${theme.co.neutral99};
          ${overlayTextShadow};
        }

        ${Heading} {
          ${theme.typography.heading.mobile.s};
        }

        ${PositionLabel} {
          display: none;
        }

        ${ChoiceText} {
          margin-inline: 0;
        }
      `
    }
  `,
);
